import React, { useState, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';

import { authSelectors } from 'app-components/flux/auth';
import { actionHelpers } from 'app-components/flux/core';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { profileActions } from 'app-components/flux/profile';
import { userInfoActions } from 'app-components/flux/user-info';
import { getEnvironmentConfig, platform, tracker } from 'app-components/utils/core';
import { localPreferences } from 'app-components/utils/core';
import { getThemeKey } from 'themes/themesSelectors';
import useSetThemeKey from 'hooks/useSetThemeKey';

import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import QSwitch from 'components/QSwitch';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import AccountDetailsCard from 'containers/Settings/ChildPages/General/AccountDetailsCard';
import QuickenAssistSettingsCard from 'containers/Settings/ChildPages/General/QuickenAssistSettingsCard';
import { SettingsFeatureCard } from 'containers/Settings/ChildPages/General/SettingsFeatureCard';
import ThemeChoiceOption from 'containers/Settings/ChildPages/General/ThemeChoiceOption';
import { THEME_CHOICES } from 'containers/Settings/ChildPages/General/themeChoiceConfig';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { subscriptionsActions } from 'app-components/flux/subscriptions';

import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles()((theme) => {
  const { spacing, breakpoints } = theme;
  return {
    contentContainer: {
      display: 'flex',
      gap: spacing(2),
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      [breakpoints.up('md')]: {
        flexWrap: 'nowrap',
      },
      [breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    mainContent: {
      flex: 1,
      [breakpoints.up('md')]: {
        minWidth: 0,
      },
      [breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    subContent: {
      minWidth: 300,
      width: '100%',
      flexShrink: 0,
      [breakpoints.up('md')]: {
        width: 'auto',
        flexBasis: '30%',
      },
      [breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  };
});

function Profile(): JSX.Element {
  const navigate = useNavigate();
  const [isChromebook, setIsChromebook] = useState(false);

  const authSession = useSelector(authSelectors.getAuthSession);
  const darkMode = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'darkMode'));

  const playBillingFeatureFlag = useSelector(featureFlagsSelectors.getFeatureFlags)?.get('playBilling');
  const aiEnabled = useSelector(featureFlagsSelectors.isAiEnabled);
  const isPlayBillingEnabled = playBillingFeatureFlag && isChromebook;

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const themeKey = useSelector(getThemeKey);
  const setThemeKey = useSetThemeKey();

  useMount(async () => {
    const featureFlagsOverrides = localPreferences.getItem('featureFlagsOverrides') || null;
    if (!featureFlagsOverrides?.['business-tier']) {
      dispatch(subscriptionsActions.getSubscriptions());
    }
    dispatch(userInfoActions.getUserInfo());
    dispatch(profileActions.getProfile());

    const chromebook = await platform.isChromebook();
    setIsChromebook(chromebook);
  });

  const handleAddSubscription = () => {
    if (isPlayBillingEnabled) {
      navigate('/pick-your-plan');
    } else {
      window.location.href = `${getEnvironmentConfig().pick_your_plan_url}?bearer=${authSession.accessToken}`;
    }
  };

  const handleGoToAccount = () => {
    tracker.track(tracker.events.myAccount, { location: 'profile' });
    window.open(`${getEnvironmentConfig().myaccount_url}?hide-nav&bearer=${authSession.accessToken}`);
  };

  const privacyMode = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'privacyMode'));
  const [sPrivacyMode, setSPrivacyMode] = usePreferenceV2('sPrivacyMode');

  const isBetaEnrolled = Boolean(authSession.groups?.includes('qcs_acme_beta'));

  const handleBetaEnrollmentChange = (_event: React.ChangeEvent<HTMLInputElement>, value: boolean): void => {
    if (value) {
      const registerForBetaPromise = actionHelpers.bindPromiseAction(dispatch, userInfoActions.userRegisterMeForBeta);
      registerForBetaPromise({ clientId: getEnvironmentConfig().client_id })
        .then((_response) => {
          dispatch(userInfoActions.getUserInfo());
        })
        .catch((_error) => {
          dispatch(userInfoActions.getUserInfo());
        });
      return;
    }

    const unregisterForBetaPromise = actionHelpers.bindPromiseAction(dispatch, userInfoActions.userUnregisterMeForBeta);
    unregisterForBetaPromise({ clientId: getEnvironmentConfig().client_id })
      .then((_response) => {
        dispatch(userInfoActions.getUserInfo());
      })
      .catch((_error) => {
        dispatch(userInfoActions.getUserInfo());
      });
  };

  return (
    <>
      <SettingsHeader
        title='General'
        description='Manage your basic profile preferences and app settings.'
        primaryItem={
          <Button id='edit-details' onClick={handleGoToAccount} endIcon={<OpenInNewRoundedIcon />} variant='outlined'>
            Manage Quicken ID
          </Button>
        }
      />
      <Box className={classes.contentContainer}>
        <Stack gap={2} className={classes.mainContent}>
          <AccountDetailsCard onAddSubscription={handleAddSubscription} />

          {aiEnabled ? <QuickenAssistSettingsCard /> : null}
        </Stack>

        <Stack gap={2} className={classes.subContent}>
          {privacyMode && (
            <SettingsFeatureCard
              id='privacy-mode'
              title='Privacy Mode'
              subtitle='Hide all the dollar values throughout the app'
            >
              <Stack direction='row' gap={2} alignContent='center' alignItems='center'>
                <Typography variant='captionMedium' color={sPrivacyMode ? 'textPrimary' : 'text.disabled'}>
                  {sPrivacyMode ? 'Privacy mode on' : 'Privacy mode off'}
                </Typography>
                <Switch
                  checked={Boolean(sPrivacyMode)}
                  onChange={(_event, checked) => {
                    if (checked) {
                      setSPrivacyMode(checked);
                      tracker.track(tracker.events.privacyMode, { state: checked, location: 'Settings' });
                    } else {
                      showConfirmationDialog({
                        title: 'Switch off Privacy mode?',
                        content: 'With privacy mode off, actual dollar amounts will become visible in Simplifi.',
                        confirmLabel: 'Switch off',
                        confirmAction: () => {
                          setSPrivacyMode(checked);
                          tracker.track(tracker.events.privacyMode, { state: checked, location: 'Settings' });
                        },
                      });
                    }
                  }}
                />
              </Stack>
              <Typography variant='captionRegular' color='text.secondary'>
                Payee names and percentages can still be visible.
              </Typography>
            </SettingsFeatureCard>
          )}

          {darkMode && (
            <SettingsFeatureCard id='appearance' title='Appearance' subtitle='Customize your experience'>
              <Stack direction='row' gap={1} alignItems='stretch' role='radiogroup' aria-label='Appearance'>
                {THEME_CHOICES.map(({ id, value, label, Icon }) => (
                  <ThemeChoiceOption
                    key={value}
                    id={id}
                    value={value}
                    label={label}
                    Icon={Icon}
                    selectedThemeKey={themeKey}
                    onSelect={setThemeKey}
                  />
                ))}
              </Stack>
            </SettingsFeatureCard>
          )}

          <SettingsFeatureCard
            id='early-access'
            title='Early Access'
            subtitle='Be the first to try the latest Quicken Simplifi innovations!'
          >
            <Stack gap={1} justifyContent='flex-start' alignItems='flex-start'>
              <Typography variant='paragraphRegular'>
                Enjoy exclusive access to upcoming features before they officially launch —plus, you can turn this off
                anytime without affecting your data.
              </Typography>
              <FormControlLabel
                id='register-for-beta'
                disabled={!authSession?.groups}
                control={
                  <QSwitch
                    showUncheckProgress
                    showCheckProgress
                    checked={isBetaEnrolled}
                    onChange={handleBetaEnrollmentChange}
                  />
                }
                label={
                  <Typography variant='captionMedium' color={isBetaEnrolled ? 'textPrimary' : 'text.disabled'}>
                    {isBetaEnrolled ? "You're enrolled" : 'Not enrolled'}
                  </Typography>
                }
                labelPlacement='start'
              />
              <Typography variant='captionRegular' color='text.secondary'>
                Please keep in mind that these features are updated more frequently and are tested less.
              </Typography>
            </Stack>
          </SettingsFeatureCard>
        </Stack>
      </Box>
    </>
  );
}

export default Profile;
